import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "@/components/controls/DataTable/DataTable";
import Text from "@/components/ui/text";
import { formatCurrency } from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/consts";

// formatCurrency now defaults to showing cents; this page keeps whole units
// until its own precision is reviewed on its own terms.
const WHOLE_UNITS = 0;

interface Props {
  mrr: number;
  arr: number;
  acv: number;
  tcv: number;
  currency?: string;
  convertedMrr?: number;
  convertedArr?: number;
  convertedAcv?: number;
  convertedTcv?: number;
  accountCurrencySymbol?: string;
  /**
   * The contract's own sign, already resolved against the account's so the two
   * halves of `<contract figure> (<account figure>)` cannot print the same
   * glyph. Re-deriving it from `currency` here is what let a USD contract on a
   * CAD account render `$1,234 ($5,678)` — both halves `$`.
   */
  contractCurrencySignOverride?: string;
}

interface FinancialRow {
  mrr: number;
  arr: number;
  acv: number;
  tcv: number;
  convertedMrr?: number;
  convertedArr?: number;
  convertedAcv?: number;
  convertedTcv?: number;
}

const FinancialRecognitionSection: React.FC<Props> = ({
  mrr,
  arr,
  acv,
  tcv,
  currency = "USD",
  convertedMrr,
  convertedArr,
  convertedAcv,
  convertedTcv,
  accountCurrencySymbol,
  contractCurrencySignOverride
}) => {
  const { t } = useTranslation();

  const data: FinancialRow[] = useMemo(
    () => [
      {
        mrr,
        arr,
        acv,
        tcv,
        convertedMrr,
        convertedArr,
        convertedAcv,
        convertedTcv
      }
    ],
    [mrr, arr, acv, tcv, convertedMrr, convertedArr, convertedAcv, convertedTcv]
  );

  const columns: ColumnDef<FinancialRow>[] = useMemo(
    () => [
      {
        accessorKey: "mrr",
        header: t("companies.commercials.mrr"),
        enableSorting: false,
        cell: ({ row }) => {
          const value = row.original.mrr;
          const converted = row.original.convertedMrr;
          return (
            <div className="flex items-center gap-2">
              <Text variant="body">
                {formatCurrency(value, currency, WHOLE_UNITS, contractCurrencySignOverride)}
              </Text>
              {converted !== undefined && accountCurrencySymbol && (
                <Text variant="bodyTertiary">
                  ({accountCurrencySymbol}
                  {converted.toLocaleString()})
                </Text>
              )}
            </div>
          );
        },
        size: 200
      },
      {
        accessorKey: "arr",
        header: t("companies.commercials.arr"),
        enableSorting: false,
        cell: ({ row }) => {
          const value = row.original.arr;
          const converted = row.original.convertedArr;
          return (
            <div className="flex items-center gap-2">
              <Text variant="body">
                {formatCurrency(value, currency, WHOLE_UNITS, contractCurrencySignOverride)}
              </Text>
              {converted !== undefined && accountCurrencySymbol && (
                <Text variant="bodyTertiary">
                  ({accountCurrencySymbol}
                  {converted.toLocaleString()})
                </Text>
              )}
            </div>
          );
        },
        size: 200
      },
      {
        accessorKey: "acv",
        header: t("companies.commercials.acv"),
        enableSorting: false,
        cell: ({ row }) => {
          const value = row.original.acv;
          const converted = row.original.convertedAcv;
          return (
            <div className="flex items-center gap-2">
              <Text variant="body">
                {formatCurrency(value, currency, WHOLE_UNITS, contractCurrencySignOverride)}
              </Text>
              {converted !== undefined && accountCurrencySymbol && (
                <Text variant="bodyTertiary">
                  ({accountCurrencySymbol}
                  {converted.toLocaleString()})
                </Text>
              )}
            </div>
          );
        },
        size: 200
      },
      {
        accessorKey: "tcv",
        header: t("companies.commercials.tcv"),
        enableSorting: false,
        cell: ({ row }) => {
          const value = row.original.tcv;
          const converted = row.original.convertedTcv;
          return (
            <div className="flex items-center gap-2">
              <Text variant="body">
                {formatCurrency(value, currency, WHOLE_UNITS, contractCurrencySignOverride)}
              </Text>
              {converted !== undefined && accountCurrencySymbol && (
                <Text variant="bodyTertiary">
                  ({accountCurrencySymbol}
                  {converted.toLocaleString()})
                </Text>
              )}
            </div>
          );
        },
        size: 200
      }
    ],
    [t, currency, accountCurrencySymbol, contractCurrencySignOverride]
  );

  return (
    <div className="flex flex-col">
      <Text variant="label" className="px-5 py-4">
        {t("companies.commercials.financialRecognition")}
      </Text>
      <DataTable
        columns={columns}
        data={data}
        height="auto"
        className="overflow-visible"
        headerClassName="border-b border-border-border1 border-t-0"
      />
    </div>
  );
};

export default FinancialRecognitionSection;
